import { classNames } from "@src/utils";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface PasswordStrengthBarProps {
  password: string;
  minLength: number;
  setIsPasswordStrong: (canSubmit: boolean) => void;
}

export default function PasswordStrengthBar({
  password,
  minLength,
  setIsPasswordStrong,
}: PasswordStrengthBarProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (computeScore(password) === 5) {
      setIsPasswordStrong(true);
    } else {
      setIsPasswordStrong(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, minLength, setIsPasswordStrong]);

  // compute the password strength
  const computeScore = (password: string) => {
    let score = 0;

    if (!password) {
      return score;
    }

    // ensure lower case letters are present
    if (/[a-z]/.test(password)) {
      score += 1;
    }

    // ensure upper case letters are present
    if (/[A-Z]/.test(password)) {
      score += 1;
    }

    // ensure numbers are present
    if (/[0-9]/.test(password)) {
      score += 1;
    }

    // ensure special characters are present
    // eslint-disable-next-line no-useless-escape
    if (/[!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/.test(password)) {
      score += 1;
    }

    // ensure the password length is sufficient
    if (password.length >= minLength) {
      score += 1;
    }

    return score;
  };

  const computeStyle = (password: string) => {
    const score = computeScore(password);
    if (score === 0) {
      return "w-0";
    } else if (score <= 2) {
      return "bg-red-500 w-1/3";
    } else if (score <= 4) {
      return "bg-red-500 w-2/3";
    } else {
      return "bg-green-500 w-full";
    }
  };

  const computeStrength = (password: string) => {
    const score = computeScore(password);
    if (score === 0) {
      return "";
    } else if (score <= 2) {
      return t("account.common.weakPwd");
    } else if (score <= 4) {
      return t("account.common.medPwd");
    } else {
      return t("account.common.strongPwd");
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-1 pt-1">
        <div className="overflow-hidden w-full h-4 text-xs flex justify-between rounded bg-gray-200">
          <div
            className={classNames(
              computeStyle(password),
              "box-border shadow-none flex text-center whitespace-nowrap text-white justify-center px-2 transition-all min-w-fit"
            )}
          >
            {computeStrength(password)}
          </div>
        </div>
      </div>
    </div>
  );
}
